"use client";

import { useContext } from "react";
import { WrapText } from "lucide-react";
import {
  CodeBlockCopyButton,
  CodeBlockDownloadButton,
  StreamdownContext,
  type ControlsConfig,
} from "streamdown";

export const fencedCodeActionButtonClassName =
  "inline-flex items-center gap-1 p-1 text-xs text-muted-foreground transition-all hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

function isCodeControlEnabled(controls: ControlsConfig, key: "copy" | "download"): boolean {
  if (typeof controls === "boolean") return controls;
  const code = controls.code;
  if (code === false) return false;
  if (code === true || code === undefined) return true;
  return code[key] !== false;
}

export function useFencedCodeBlockOptions(meta?: string) {
  const { controls, lineNumbers } = useContext(StreamdownContext);
  const hideLineNumbers = Boolean(meta && /\bnoLineNumbers\b/.test(meta));
  const startLineToken = meta?.match(/\bstartLine=(\d+)/)?.[1];
  const parsedStart = startLineToken ? Number.parseInt(startLineToken, 10) : Number.NaN;
  return {
    lineNumbers: lineNumbers && !hideLineNumbers,
    startLine: Number.isInteger(parsedStart) && parsedStart >= 1 ? parsedStart : undefined,
    copy: isCodeControlEnabled(controls, "copy"),
    download: isCodeControlEnabled(controls, "download"),
  };
}

export function wrapCodeClassName(wrap: boolean): string | undefined {
  // Streamdown applies a custom class to the block chrome and body. Target the
  // source elements explicitly so wrapping remains effective in either path.
  return wrap
    ? "[&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_code]:whitespace-pre-wrap [&_code]:break-words"
    : undefined;
}

export function WrapCodeButton({ wrap, onClick }: { wrap: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={wrap}
      className={fencedCodeActionButtonClassName}
    >
      <WrapText className="size-3.5" />
      {wrap ? "Unwrap" : "Wrap"}
    </button>
  );
}

export function FencedCodeCopyDownload({
  code,
  language,
  copy,
  download,
}: {
  code: string;
  language: string;
  copy: boolean;
  download: boolean;
}) {
  return (
    <>
      {download ? <CodeBlockDownloadButton code={code} language={language} /> : null}
      {copy ? <CodeBlockCopyButton code={code} /> : null}
    </>
  );
}
